import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Tag } from '../ui/primitives';
import { InstructorMeterStrip } from '../ui/InstructorMeterStrip';
import { INSTRUCTOR_METERS } from '../services/instructorMeterLimits';
import { SETTINGS_FIELD_HELP as HELP } from '../ui/settingsFieldHelp';
import { formatEntitlementLabel, formatPlanLabel } from './Packages/planDisplayUtils';

const PlanHead = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 4px;
`;

const PlanCopy = styled.div`
  flex: 1;
  min-width: 200px;

  h3 {
    margin: 8px 0 2px;
    font-size: 22px;
    font-weight: 760;
    color: ${({ theme }) => theme.text};
  }

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.fsSm};
    color: ${({ theme }) => theme.textMuted};
  }
`;

const PlanDate = styled.p`
  margin: 8px 0 0 !important;
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 650;
  color: ${({ theme }) => theme.text} !important;
`;

const FeatureWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 14px;
`;

const FeatureLabel = styled.div`
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 700;
  color: ${({ theme }) => theme.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
`;

const BILLING_METERS = [
  { key: INSTRUCTOR_METERS.courses, label: 'Courses', help: HELP.usageCourses },
  { key: INSTRUCTOR_METERS.students, label: 'Learners', help: HELP.usageStudents },
  { key: INSTRUCTOR_METERS.assessments, label: 'Assessments', help: HELP.usageAssessments },
];

/** Enabled feature keys from the live entitlements contract. */
export function listEnabledEntitlementFeatures(features) {
  if (!features || typeof features !== 'object') {
    return [];
  }
  return Object.entries(features)
    .filter(([, enabled]) => enabled === true)
    .map(([key]) => ({
      key,
      label: formatEntitlementLabel(key),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** epoch millis → readable date, or null if unusable. */
function fmtPlanDate(ms) {
  if (ms === null || ms === undefined || ms === '') return null;
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Current plan tier, usage meters, and enabled features for Billing. */
export default function BillingPlanOverview({ entitlements }) {
  const tierLabel = formatPlanLabel(entitlements?.tier || 'starter');
  const roleLabel =
    entitlements?.role === 'owner'
      ? 'Organization owner'
      : entitlements?.role === 'admin'
        ? 'Organization admin'
        : 'Instructor';
  const enabledFeatures = useMemo(
    () => listEnabledEntitlementFeatures(entitlements?.features),
    [entitlements?.features],
  );

  return (
    <>
      <PlanHead>
        <PlanCopy>
          <Tag $tone="accent">Current plan</Tag>
          <h3>{tierLabel}</h3>
          <p>{roleLabel} · usage counts against your organization plan limits</p>
          {fmtPlanDate(entitlements?.nextPlanDate) && (
            <PlanDate>Plan valid until {fmtPlanDate(entitlements.nextPlanDate)}</PlanDate>
          )}
        </PlanCopy>
      </PlanHead>

      <InstructorMeterStrip entitlements={entitlements} meters={BILLING_METERS} />

      {enabledFeatures.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <FeatureLabel>Included on your plan</FeatureLabel>
          <FeatureWrap>
            {enabledFeatures.map((feature) => (
              <Tag key={feature.key} $tone="green">{feature.label}</Tag>
            ))}
          </FeatureWrap>
        </div>
      )}
    </>
  );
}
